import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Allotment } from 'allotment';
import 'allotment/dist/style.css';
import {
  FileCode,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  FolderOpen,
  FileOutput,
  Code,
  Shield,
} from 'lucide-react';
import { EditorPanel } from './EditorPanel';
import { OutputPanel } from './OutputPanel';
import { SecurityPanel } from './SecurityPanel';
import { useStore } from '../hooks/useStore';
import { useDebounce } from '../hooks/useDebounce';
import { sessionApi, renderApi, securityApi } from '../services/api';
import { HelmWebSocket } from '../services/websocket';
import type { RenderResult, EditorTab, ChartVersion, SecurityScanResult } from '../types';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// Mobile view types
type MobileView = 'explorer' | 'editor' | 'output' | 'security';

// Output panel tab types
type OutputTab = 'output' | 'security';

export default function EditorPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const {
    setSession,
    values,
    setValues,
    templates,
    setTemplates,
    updateTemplate,
    activeTab,
    setActiveTab,
    editorTabs,
    setEditorTabs,
    addEditorTab,
    removeEditorTab,
    renderResult,
    setRenderResult,
    isRendering,
    setIsRendering,
  } = useStore();

  const [isLoading, setIsLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['templates']));
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(false);
  const [releaseName, setReleaseName] = useState('release-name');
  const [namespace, setNamespace] = useState('default');
  const [chartVersion, setChartVersion] = useState('');
  const [chartName, setChartName] = useState('');
  const [repository, setRepository] = useState('');
  const [availableVersions, setAvailableVersions] = useState<ChartVersion[]>([]);
  const [isChangingVersion, setIsChangingVersion] = useState(false);
  const [mobileView, setMobileView] = useState<MobileView>('editor');
  const [isMobile, setIsMobile] = useState(false);
  const [originalValues, setOriginalValues] = useState<string>('');
  const [outputTab, setOutputTab] = useState<OutputTab>('output');
  const [securityScanResult, setSecurityScanResult] = useState<SecurityScanResult | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const wsRef = useRef<HelmWebSocket | null>(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load session
  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    const loadSession = async () => {
      try {
        setIsLoading(true);
        const sessionData = await sessionApi.get(sessionId);
        setSession(sessionData);
        setValues(sessionData.values);
        setOriginalValues(sessionData.values);
        setTemplates(sessionData.templates);
        setChartVersion(sessionData.chartVersion);
        setChartName(sessionData.chartName);
        setRepository(sessionData.repository);

        // Set up editor tabs
        const tabs: EditorTab[] = [
          {
            id: 'values',
            name: 'values.yaml',
            type: 'values',
            content: sessionData.values,
            language: 'yaml',
          },
        ];
        setEditorTabs(tabs);
        setActiveTab('values');

        // Initial render
        const result = await renderApi.render(sessionId, sessionData.values, undefined, releaseName, namespace);
        setRenderResult(result);

        // Load available versions
        try {
          const versions = await sessionApi.getVersions(sessionId);
          setAvailableVersions(versions);
        } catch (error) {
          console.error('Failed to load versions:', error);
        }
      } catch (error) {
        toast.error('Failed to load session');
        console.error(error);
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    loadSession();
  }, [sessionId]);

  // WebSocket connection
  useEffect(() => {
    if (!sessionId || isLoading) return;

    wsRef.current = new HelmWebSocket(
      sessionId,
      (result: RenderResult) => {
        setRenderResult(result);
        setIsRendering(false);
      },
      (error: string) => {
        toast.error(error);
        setIsRendering(false);
      }
    );

    return () => {
      wsRef.current?.close();
    };
  }, [sessionId, isLoading]);

  // Debounced render for values
  const debouncedRenderValues = useDebounce((newValues: string, newReleaseName?: string, newNamespace?: string) => {
    if (wsRef.current) {
      setIsRendering(true);
      wsRef.current.updateValues(newValues, newReleaseName, newNamespace);
    }
  }, 300);

  // Debounced render for templates
  const debouncedRenderTemplate = useDebounce((name: string, content: string) => {
    if (wsRef.current) {
      setIsRendering(true);
      wsRef.current.updateTemplate(name, content);
    }
  }, 300);

  const handleValuesChange = useCallback(
    (value: string) => {
      setValues(value);
      debouncedRenderValues(value, releaseName, namespace);
    },
    [debouncedRenderValues, releaseName, namespace, setValues]
  );

  const handleReleaseNameChange = useCallback(
    (newReleaseName: string) => {
      setReleaseName(newReleaseName);
      debouncedRenderValues(values, newReleaseName, namespace);
    },
    [debouncedRenderValues, values, namespace]
  );

  const handleNamespaceChange = useCallback(
    (newNamespace: string) => {
      setNamespace(newNamespace);
      debouncedRenderValues(values, releaseName, newNamespace);
    },
    [debouncedRenderValues, values, releaseName]
  );

  const handleVersionChange = useCallback(
    async (newVersion: string) => {
      if (!sessionId || newVersion === chartVersion || isChangingVersion) return;

      setIsChangingVersion(true);
      try {
        toast.loading('Changing version...', { id: 'change-version' });
        const { session: updatedSession, templates: newTemplates } = await sessionApi.changeVersion(sessionId, newVersion);

        // Update local state
        setChartVersion(updatedSession.chartVersion);
        setValues(updatedSession.values);
        setOriginalValues(updatedSession.values);
        setTemplates(newTemplates);
        setSession(updatedSession);

        // Reset editor tabs to show new values
        const tabs: EditorTab[] = [
          {
            id: 'values',
            name: 'values.yaml',
            type: 'values',
            content: updatedSession.values,
            language: 'yaml',
          },
        ];
        setEditorTabs(tabs);
        setActiveTab('values');

        // Re-render with new values
        const result = await renderApi.render(sessionId, updatedSession.values, undefined, releaseName, namespace);
        setRenderResult(result);

        toast.success(`Changed to version ${newVersion}`, { id: 'change-version' });
      } catch (error) {
        toast.error('Failed to change version', { id: 'change-version' });
        console.error(error);
      } finally {
        setIsChangingVersion(false);
      }
    },
    [sessionId, chartVersion, isChangingVersion, releaseName, namespace, setSession, setValues, setTemplates, setEditorTabs, setActiveTab, setRenderResult]
  );

  const handleTemplateChange = useCallback(
    (templateName: string, value: string) => {
      updateTemplate(templateName, value);
      debouncedRenderTemplate(templateName, value);
    },
    [debouncedRenderTemplate, updateTemplate]
  );

  const handleOpenTemplate = (name: string) => {
    const content = templates[name] || '';
    addEditorTab({
      id: `template-${name}`,
      name,
      type: 'template',
      content,
      language: 'yaml',
    });
    // Switch to editor view on mobile
    if (isMobile) {
      setMobileView('editor');
    }
  };

  const handleOpenManifest = (name: string, content: string) => {
    addEditorTab({
      id: `manifest-${name}`,
      name: `[output] ${name}`,
      type: 'manifest',
      content,
      language: 'yaml',
      readOnly: true,
    });
    // Switch to editor view on mobile
    if (isMobile) {
      setMobileView('editor');
    }
  };

  const handleResetValues = useCallback(() => {
    if (values === originalValues) {
      toast('Values already match the original', { icon: 'ℹ️' });
      return;
    }
    setValues(originalValues);
    debouncedRenderValues(originalValues, releaseName, namespace);
    toast.success('Values reset to original');
  }, [values, originalValues, setValues, debouncedRenderValues, releaseName, namespace]);

  const handleSecurityScan = useCallback(async () => {
    if (!renderResult?.manifests || Object.keys(renderResult.manifests).length === 0) {
      toast.error('No manifests to scan');
      return;
    }

    setIsScanning(true);
    try {
      const result = await securityApi.scan(renderResult.manifests);
      setSecurityScanResult(result);

      // Show summary toast
      if (result.summary.criticalCount > 0) {
        toast.error(`Found ${result.summary.criticalCount} critical issue(s)`, { duration: 4000 });
      } else if (result.summary.warningCount > 0) {
        toast(`Found ${result.summary.warningCount} recommendation(s)`, { icon: '⚠️', duration: 3000 });
      } else if (result.summary.totalResources > 0) {
        toast.success('No security issues found!', { duration: 3000 });
      } else {
        toast('No scannable resources found', { icon: 'ℹ️' });
      }
    } catch (error) {
      console.error('Security scan failed:', error);
      toast.error('Security scan failed');
    } finally {
      setIsScanning(false);
    }
  }, [renderResult?.manifests]);

  const handleNavigateToResource = useCallback((fileName: string) => {
    if (!renderResult?.manifests) return;

    // Find the manifest content by fileName
    const manifestContent = renderResult.manifests[fileName];
    if (manifestContent) {
      handleOpenManifest(fileName, manifestContent);
      // Switch to output tab on desktop to show context
      setOutputTab('output');
    } else {
      toast.error(`Manifest "${fileName}" not found`);
    }
  }, [renderResult?.manifests, handleOpenManifest]);

  const toggleFolder = (folder: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  const getCurrentContent = useCallback(() => {
    const tab = editorTabs.find((t) => t.id === activeTab);
    if (!tab) return '';

    if (tab.type === 'values') {
      return values;
    } else if (tab.type === 'template') {
      const templateName = tab.name;
      return templates[templateName] || '';
    } else if (tab.type === 'manifest') {
      const manifestName = tab.name.replace('[output] ', '');
      return renderResult?.manifests[manifestName] || '';
    }
    return '';
  }, [activeTab, editorTabs, values, templates, renderResult?.manifests]);

  const handleEditorChange = useCallback((value: string) => {
    const tab = editorTabs.find((t) => t.id === activeTab);
    if (!tab) return;

    if (tab.type === 'values') {
      handleValuesChange(value);
    } else if (tab.type === 'template') {
      handleTemplateChange(tab.name, value);
    }
  }, [activeTab, editorTabs, handleValuesChange, handleTemplateChange]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-dark-400">Loading chart...</p>
        </div>
      </div>
    );
  }

  const activeTabData = editorTabs.find((t) => t.id === activeTab);

  // Mobile bottom navigation
  const MobileNavigation = () => (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-dark-850 border-t border-dark-700 z-40 safe-area-bottom">
      <div className="flex items-center justify-around h-14">
        <button
          onClick={() => setMobileView('explorer')}
          className={clsx(
            'flex flex-col items-center justify-center flex-1 h-full transition-colors',
            mobileView === 'explorer' ? 'text-accent-primary' : 'text-dark-400'
          )}
        >
          <FolderOpen className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Files</span>
        </button>
        <button
          onClick={() => setMobileView('editor')}
          className={clsx(
            'flex flex-col items-center justify-center flex-1 h-full transition-colors',
            mobileView === 'editor' ? 'text-accent-primary' : 'text-dark-400'
          )}
        >
          <Code className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Editor</span>
        </button>
        <button
          onClick={() => setMobileView('output')}
          className={clsx(
            'flex flex-col items-center justify-center flex-1 h-full transition-colors relative',
            mobileView === 'output' ? 'text-accent-primary' : 'text-dark-400'
          )}
        >
          <FileOutput className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Output</span>
          {/* Error/success indicator */}
          {renderResult?.errors && renderResult.errors.length > 0 ? (
            <span className="absolute top-1 right-1/4 w-2 h-2 bg-accent-error rounded-full" />
          ) : renderResult && Object.keys(renderResult.manifests || {}).length > 0 ? (
            <span className="absolute top-1 right-1/4 w-2 h-2 bg-accent-success rounded-full" />
          ) : null}
        </button>
        <button
          onClick={() => setMobileView('security')}
          className={clsx(
            'flex flex-col items-center justify-center flex-1 h-full transition-colors relative',
            mobileView === 'security' ? 'text-accent-primary' : 'text-dark-400'
          )}
        >
          <Shield className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Lint</span>
          {/* Security indicator */}
          {securityScanResult?.summary.criticalCount ? (
            <span className="absolute top-1 right-1/4 w-2 h-2 bg-red-500 rounded-full" />
          ) : securityScanResult?.summary.warningCount ? (
            <span className="absolute top-1 right-1/4 w-2 h-2 bg-yellow-500 rounded-full" />
          ) : null}
        </button>
      </div>
    </div>
  );

  // File Explorer Panel (used in both mobile and desktop)
  const FileExplorerPanel = ({ className }: { className?: string }) => (
    <div className={clsx("h-full bg-dark-850 flex flex-col", className)}>
      {/* Chart Info Header */}
      <div className="p-3 md:p-2 border-b border-dark-700">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xs md:text-[10px] font-semibold text-dark-400 uppercase tracking-wider">
            Chart
          </h2>
          {isChangingVersion && (
            <RefreshCw className="w-3 h-3 text-accent-primary animate-spin" />
          )}
        </div>
        <div className="text-sm md:text-[11px] text-dark-200 font-medium truncate" title={chartName}>
          {chartName || 'Loading...'}
        </div>
        <div className="text-xs md:text-[10px] text-dark-500 truncate" title={repository}>
          {repository}
        </div>
        {/* Version Selector */}
        {availableVersions.length > 0 ? (
          <div className="mt-2">
            <label className="text-[10px] md:text-[9px] text-dark-500 uppercase tracking-wider block mb-0.5">
              Version
            </label>
            <select
              value={chartVersion}
              onChange={(e) => handleVersionChange(e.target.value)}
              disabled={isChangingVersion}
              className="w-full px-2 py-1.5 md:px-1.5 md:py-1 text-sm md:text-[11px] bg-dark-800 border border-dark-600 rounded text-dark-200 focus:outline-none focus:border-accent-primary disabled:opacity-50 cursor-pointer"
            >
              {availableVersions.map((v) => (
                <option key={v.version} value={v.version}>
                  {v.version}
                  {v.appVersion && ` (app: ${v.appVersion})`}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mt-1.5 text-xs md:text-[10px] text-dark-400">
            v{chartVersion}
          </div>
        )}
      </div>

      {/* File Explorer Header */}
      <div className="p-3 md:p-2 border-b border-dark-700">
        <h2 className="text-xs md:text-[10px] font-semibold text-dark-400 uppercase tracking-wider">
          Explorer
        </h2>
      </div>
      <div className="flex-1 overflow-y-auto p-2 md:p-1.5 scrollbar-hide-mobile">
        {/* Values file */}
        <FileTreeItem
          name="values.yaml"
          icon={<FileText className="w-4 h-4 md:w-3 md:h-3 text-yellow-500" />}
          isActive={activeTab === 'values'}
          onClick={() => {
            setActiveTab('values');
            if (isMobile) setMobileView('editor');
          }}
        />

        {/* Templates folder */}
        <div className="mt-2 md:mt-1.5">
          <div
            className="flex items-center gap-1.5 md:gap-1 px-2 md:px-1.5 py-1.5 md:py-0.5 cursor-pointer hover:bg-dark-700 rounded text-sm md:text-[11px]"
            onClick={() => toggleFolder('templates')}
          >
            {expandedFolders.has('templates') ? (
              <ChevronDown className="w-4 h-4 md:w-3 md:h-3 text-dark-500" />
            ) : (
              <ChevronRight className="w-4 h-4 md:w-3 md:h-3 text-dark-500" />
            )}
            <span className="text-dark-300">templates</span>
          </div>
          {expandedFolders.has('templates') && (
            <div className="ml-4 md:ml-3">
              {Object.keys(templates).map((name) => (
                <FileTreeItem
                  key={name}
                  name={name}
                  icon={<FileCode className="w-4 h-4 md:w-3 md:h-3 text-blue-500" />}
                  isActive={activeTab === `template-${name}`}
                  onClick={() => handleOpenTemplate(name)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Status bar */}
      <div className="border-t border-dark-700 p-2 md:p-1.5">
        <div className="flex items-center gap-2 md:gap-1.5 text-xs md:text-[10px]">
          {isRendering ? (
            <>
              <Clock className="w-3 h-3 md:w-2.5 md:h-2.5 text-accent-warning animate-pulse" />
              <span className="text-dark-400">Rendering...</span>
            </>
          ) : renderResult?.errors && renderResult.errors.length > 0 ? (
            <>
              <AlertCircle className="w-3 h-3 md:w-2.5 md:h-2.5 text-accent-error" />
              <span className="text-accent-error">
                {renderResult.errors.length} error(s)
              </span>
            </>
          ) : (
            <>
              <CheckCircle className="w-3 h-3 md:w-2.5 md:h-2.5 text-accent-success" />
              <span className="text-dark-400">
                {renderResult?.renderTimeMs || 0}ms
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );



  // Mobile Layout
  if (isMobile) {
    return (
      <div className="h-full flex flex-col pb-14">
        {/* Mobile Views */}
        <div className="flex-1 overflow-hidden">
          {mobileView === 'explorer' && (
            <FileExplorerPanel className="border-r-0" />
          )}
          {mobileView === 'editor' && (
            <EditorPanel
              editorTabs={editorTabs}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              removeEditorTab={removeEditorTab}
              currentContent={getCurrentContent()}
              onEditorChange={handleEditorChange}
              isReadOnly={activeTabData?.readOnly || false}
              isMobile={isMobile}
              renderResult={renderResult}
              onResetValues={handleResetValues}
              canResetValues={values !== originalValues}
            />
          )}
          {mobileView === 'output' && (
            <OutputPanel
              isCollapsible={false}
              className="border-l-0"
              isOutputCollapsed={isOutputCollapsed}
              setIsOutputCollapsed={setIsOutputCollapsed}
              isRendering={isRendering}
              renderResult={renderResult}
              releaseName={releaseName}
              namespace={namespace}
              onReleaseNameChange={handleReleaseNameChange}
              onNamespaceChange={handleNamespaceChange}
              onOpenManifest={handleOpenManifest}
            />
          )}
          {mobileView === 'security' && (
            <SecurityPanel
              scanResult={securityScanResult}
              isScanning={isScanning}
              onScan={handleSecurityScan}
              hasManifests={Boolean(renderResult?.manifests && Object.keys(renderResult.manifests).length > 0)}
              onNavigateToResource={handleNavigateToResource}
            />
          )}
        </div>

        {/* Mobile Bottom Navigation */}
        <MobileNavigation />
      </div>
    );
  }

  // Desktop Layout with Allotment
  return (
    <div className="h-full flex">
      <Allotment>
        {/* File Explorer */}
        <Allotment.Pane preferredSize={200} minSize={150} maxSize={300}>
          <FileExplorerPanel className="border-r border-dark-700" />
        </Allotment.Pane>

        {/* Editor Area */}
        <Allotment.Pane>
          <EditorPanel
            editorTabs={editorTabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            removeEditorTab={removeEditorTab}
            currentContent={getCurrentContent()}
            onEditorChange={handleEditorChange}
            isReadOnly={activeTabData?.readOnly || false}
            isMobile={isMobile}
            renderResult={renderResult}
            onResetValues={handleResetValues}
            canResetValues={values !== originalValues}
          />
        </Allotment.Pane>

        {/* Output/Security Sidebar */}
        <Allotment.Pane
          preferredSize={isOutputCollapsed ? 36 : 350}
          minSize={isOutputCollapsed ? 36 : 250}
          maxSize={isOutputCollapsed ? 36 : 500}
        >
          <div className="h-full flex flex-col border-l border-dark-700">
            {/* Tabs - only show when not collapsed */}
            {!isOutputCollapsed && (
              <div className="flex items-center border-b border-dark-700 bg-dark-850">
                <button
                  onClick={() => setOutputTab('output')}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors',
                    outputTab === 'output'
                      ? 'text-dark-100 border-b-2 border-accent-primary'
                      : 'text-dark-400 hover:text-dark-200'
                  )}
                >
                  <FileOutput className="w-3.5 h-3.5" />
                  Output
                </button>
                <button
                  onClick={() => setOutputTab('security')}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors relative',
                    outputTab === 'security'
                      ? 'text-dark-100 border-b-2 border-accent-primary'
                      : 'text-dark-400 hover:text-dark-200'
                  )}
                >
                  <Shield className="w-3.5 h-3.5" />
                  Lint
                  {securityScanResult?.summary.criticalCount ? (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-red-500/20 text-red-400 rounded-full">
                      {securityScanResult.summary.criticalCount}
                    </span>
                  ) : securityScanResult?.summary.warningCount ? (
                    <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-yellow-500/20 text-yellow-400 rounded-full">
                      {securityScanResult.summary.warningCount}
                    </span>
                  ) : null}
                </button>
              </div>
            )}
            {/* Panel Content */}
            <div className="flex-1 overflow-hidden">
              {outputTab === 'output' ? (
                <OutputPanel
                  isOutputCollapsed={isOutputCollapsed}
                  setIsOutputCollapsed={setIsOutputCollapsed}
                  isRendering={isRendering}
                  renderResult={renderResult}
                  releaseName={releaseName}
                  namespace={namespace}
                  onReleaseNameChange={handleReleaseNameChange}
                  onNamespaceChange={handleNamespaceChange}
                  onOpenManifest={handleOpenManifest}
                />
              ) : (
                <SecurityPanel
                  scanResult={securityScanResult}
                  isScanning={isScanning}
                  onScan={handleSecurityScan}
                  hasManifests={Boolean(renderResult?.manifests && Object.keys(renderResult.manifests).length > 0)}
                  onNavigateToResource={handleNavigateToResource}
                />
              )}
            </div>
          </div>
        </Allotment.Pane>
      </Allotment>
    </div>
  );
}

function FileTreeItem({
  name,
  icon,
  isActive,
  onClick,
}: {
  name: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <div
      className={clsx(
        'flex items-center gap-2 md:gap-1.5 px-2 md:px-1.5 py-2 md:py-0.5 rounded cursor-pointer transition-colors',
        isActive ? 'bg-accent-primary/20 text-accent-primary' : 'hover:bg-dark-700 text-dark-300'
      )}
      onClick={onClick}
    >
      {icon}
      <span className="text-sm md:text-[11px] truncate">{name}</span>
    </div>
  );
}
